import type { CreateDepartmentInput, Department, DepartmentImpact } from "@closer/shared";
import { createDepartmentSchema } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { ContextActivityModal } from "../../components/ContextActivityModal";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * Departmanlar (CLOS-190 `[052][S02]`).
 *
 * ### ⚠ Bu sayfada kullanıcı ATANMAZ
 *
 * Kart açık: *"Departmanlar sayfasında bağlı kullanıcılar YALNIZ SALT OKUNUR gösterilir;
 * ekleme/çıkarma aksiyonu bulunmaz"* ve *"kullanıcı atama veya bağlantı kaldırma CLOS-189
 * Kullanıcılar ve Üyelikler sayfasına departman filtresi ve güvenli bağlamla
 * yönlendirilir."*
 *
 * Bu yüzden satırda yalnız bir SAYI ve bir BAĞLANTI var. Atamayı iki sayfaya birden koymak,
 * aynı komutun iki yerde iki farklı biçimde çağrılması ve gün gelip ayrışması demekti.
 *
 * ### Arşivleme kararı, etkisini GÖRDÜKTEN sonra
 *
 * Etki özeti ayrı bir uçtan ve onay penceresi açılırken çekiliyor: arşivleme yanıtında
 * dönseydi yönetici neyi etkilediğini ancak etkiledikten sonra öğrenirdi.
 */

type Onay =
  | { kind: "archive"; department: Department }
  | { kind: "reactivate"; department: Department }
  | { kind: "edit"; department: Department };

export function DepartmentsPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const navigate = useNavigate();

  const [arsivliler, setArsivliler] = useState(false);
  const [onay, setOnay] = useState<Onay | null>(null);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "departments", String(arsivliler)),
    queryFn: async () =>
      (
        await api<Department[]>(`/api/departments?includeArchived=${String(arsivliler)}`, {
          tenantId,
        })
      ).data,
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Departmanlar yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <StateView
        state={resolveErrorState(sorgu.error, {
          idempotent: true,
          existenceIsSecret: false,
          overrides: {
            error: {
              title: "Departmanlar yüklenemedi",
              description: "Liste getirilemedi. Lütfen tekrar deneyin.",
            },
          },
        })}
        onRetry={() => void sorgu.refetch()}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader
        title="Departmanlar"
        description="Organizasyonunuzdaki departmanları oluşturun, düzenleyin ve arşivleyin."
      />

      <Card title="Departman ekle">
        <DepartmentForm tenantId={tenantId} arsivliler={arsivliler} />
      </Card>

      <Card title="Departmanlar">
        <div className="flex flex-col gap-4">
          <Checkbox
            label="Arşivlenenleri göster"
            checked={arsivliler}
            onChange={(event) => setArsivliler(event.target.checked)}
          />

          {sorgu.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">Henüz departman yok</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border-default">
              {sorgu.data.map((departman) => (
                <li
                  key={departman.id}
                  data-testid="departman-satiri"
                  className="flex flex-wrap items-center justify-between gap-2 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-fg-strong">{departman.name}</p>
                    {departman.description ? (
                      <p className="truncate text-sm text-fg-muted">{departman.description}</p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {departman.archivedAt ? <Badge tone="warning">Arşivli</Badge> : null}
                    <MemberCount tenantId={tenantId} departmentId={departman.id} />
                    {/*
                      ⚠ Atama YOK, YÖNLENDİRME var: komut CLOS-189'un sayfasında ve iki
                      sayfaya birden koymak, aynı komutun iki farklı çağrılış biçimini
                      ve gün gelip ayrışmasını getirirdi.
                    */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        void navigate(`/app/organizasyon/kullanicilar?departman=${departman.id}`)
                      }
                    >
                      Kullanıcıları görüntüle
                    </Button>
                    {/*
                      ⚠ DEPARTMAN AKTİVİTESİNİN TÜKETİCİSİ (CLOS-881): satırlar üretiliyor,
                      kapı (`organization.department`) yazılı, soran yüzey yoktu.
                    */}
                    <ContextActivityModal
                      entityType="department"
                      entityId={departman.id}
                      baslik={departman.name}
                    />
                    {departman.archivedAt ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOnay({ kind: "reactivate", department: departman })}
                      >
                        Yeniden aktifleştir
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOnay({ kind: "edit", department: departman })}
                        >
                          Düzenle
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOnay({ kind: "archive", department: departman })}
                        >
                          Arşivle
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {onay ? (
        <DepartmentDialog
          tenantId={tenantId}
          arsivliler={arsivliler}
          onay={onay}
          onClose={() => setOnay(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Bağlı kullanıcı SAYISI — salt okunur.
 *
 * Ayrı bir sorgu: sayı departmanın kendi kaydında tutulsaydı her atama/çıkarma iki tabloyu
 * birden güncellemek zorunda kalır ve ikisi bir gün ayrışırdı. Sayacı türetmek, onu
 * saklamaktan her zaman daha doğru.
 */
function MemberCount({ tenantId, departmentId }: { tenantId: string; departmentId: string }) {
  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "department-impact", departmentId),
    queryFn: async () =>
      (await api<DepartmentImpact>(`/api/departments/${departmentId}/impact`, { tenantId })).data,
  });

  if (!sorgu.data) return null;
  return <span className="text-sm text-fg-muted">{sorgu.data.memberships} kullanıcı</span>;
}

function DepartmentForm({ tenantId, arsivliler }: { tenantId: string; arsivliler: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useZodForm(createDepartmentSchema);

  const gonder = form.handleSubmit(async (girdi: CreateDepartmentInput) => {
    try {
      await api("/api/departments", {
        method: "POST",
        body: JSON.stringify({ name: girdi.name }),
        tenantId,
      });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "departments", String(arsivliler)),
      });
      form.reset();
      toast.success("Departman oluşturuldu");
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields) {
        applyServerErrors(form, error);
        return;
      }
      /**
       * ⚠ SUNUCUNUN cümlesi: arşivli bir adla çakışıldığında sunucu *"yeniden
       * aktifleştirebilirsiniz"* diyor ve bu, kullanıcının atacağı adımı söyleyen tek
       * cümle. Kendi metnimizi yazsaydık o yönlendirme kaybolurdu.
       */
      toast.error(error instanceof ApiRequestError ? error.message : "Departman oluşturulamadı.");
    }
  });

  return (
    <form noValidate className="flex flex-col gap-3" onSubmit={gonder}>
      <FormField label="Departman adı" error={form.formState.errors.name?.message} required>
        <Input placeholder="Örn. Hukuk" {...form.register("name")} />
      </FormField>
      <div className="flex justify-end">
        <Button type="submit" loading={form.formState.isSubmitting}>
          Departman ekle
        </Button>
      </div>
    </form>
  );
}

function DepartmentDialog({
  tenantId,
  arsivliler,
  onay,
  onClose,
}: {
  tenantId: string;
  arsivliler: boolean;
  onay: Onay;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [ad, setAd] = useState(onay.department.name);

  /**
   * Etki özeti KARARDAN ÖNCE. Arşivleme yanıtında dönseydi yönetici neyi etkilediğini
   * ancak etkiledikten sonra öğrenirdi (`[041]`'in ayrılma ön izlemesiyle aynı gerekçe).
   *
   * ⚠ Anahtar satırdaki sayaçla AYNI, dolayısıyla pencereyi açmak yeni bir istek üretmiyor.
   * Bir zamanlar burada `enabled: onay.kind === "archive"` vardı; diş kontrolünde ölçüldü ve
   * ISIRMADI — koşul kaldırıldığında hiçbir test değişmedi, çünkü veri zaten önbellekteydi.
   * Ölü bir bayrağı "önlem" diye tutmak, okuyanı olmayan bir kural bırakmak olurdu.
   */
  const etki = useQuery({
    queryKey: tenantKey(tenantId, "department-impact", onay.department.id),
    queryFn: async () =>
      (
        await api<DepartmentImpact>(`/api/departments/${onay.department.id}/impact`, {
          tenantId,
        })
      ).data,
  });

  const komut = useMutation({
    mutationFn: async () => {
      if (onay.kind === "edit") {
        await api(`/api/departments/${onay.department.id}`, {
          method: "PATCH",
          body: JSON.stringify({ name: ad, version: onay.department.version }),
          tenantId,
        });
        return;
      }
      await api(`/api/departments/${onay.department.id}/${onay.kind}`, {
        method: "POST",
        tenantId,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "departments", String(arsivliler)),
        }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "department-impact", onay.department.id),
        }),
      ]);
      toast.success(
        onay.kind === "edit"
          ? "Departman güncellendi"
          : onay.kind === "archive"
            ? "Departman arşivlendi"
            : "Departman yeniden aktifleştirildi",
      );
      onClose();
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  const baslik =
    onay.kind === "edit"
      ? "Departmanı düzenle"
      : onay.kind === "archive"
        ? `${onay.department.name} arşivlensin mi?`
        : `${onay.department.name} yeniden aktifleştirilsin mi?`;

  return (
    <Modal
      open
      onClose={onClose}
      title={baslik}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={komut.isPending} onClick={() => komut.mutate()}>
            {onay.kind === "edit" ? "Kaydet" : "Onayla"}
          </Button>
        </div>
      }
    >
      {onay.kind === "edit" ? (
        <FormField label="Departman adı" required>
          <Input value={ad} onChange={(event) => setAd(event.target.value)} />
        </FormField>
      ) : onay.kind === "archive" ? (
        <div className="flex flex-col gap-2 text-sm text-fg-muted">
          <p>
            Bu departmana bağlı{" "}
            <span className="font-medium text-fg-strong">{etki.data?.memberships ?? 0}</span>{" "}
            kullanıcı var.
          </p>
          <p>
            Arşivleme bir silme değildir: tarihsel ilişkiler korunur, departman yalnız yeni
            atamalarda seçilemez.
          </p>
        </div>
      ) : (
        /*
          ⚠ Kartın no-auto-restore kuralı KULLANICIYA SÖYLENİYOR. Sessizce yapsaydık
          yönetici eski ekibin geri geldiğini sanır ve kimseyi yeniden atamazdı.
        */
        <p className="text-sm text-fg-muted">
          Departman yeniden kullanıma açılır; eski kullanıcı bağlantıları geri gelmez.
        </p>
      )}
    </Modal>
  );
}
